import {inject, injectable} from 'inversify'
import {action, computed, makeObservable, observable} from 'mobx'
import {
  CompanyRecipientStrategyInterface,
  RecipientFormStrategyInterface,
  RecipientStrategyFactory,
} from '@/src/ports/services/reqipient-strategy'
import {
  type CompanyInfoByInn,
  type CompanyInfoServiceInterface,
} from '@/src/ports/services/company-info-service'
import {PersonRecipientStrategy} from './PersonRecipientStrategy'
import {DI} from '@/src/config/di/types'

const daDataCompanyFilter = (company: CompanyInfoByInn) => company.name && company.inn && company.kpp

@injectable()
export class CompanyRecipientStrategy implements CompanyRecipientStrategyInterface {
  readonly type = 'company' as const

  @observable
  company: Nullable<CompanyInfoByInn> = null

  @observable
  companies: CompanyInfoByInn[] = []

  @observable
  isLoading = false

  @observable
  openedModal = false

  @observable
  openedPhoneModal = false

  constructor(@inject(DI.CompanyInfoServiceInterface) private companyInfoService: CompanyInfoServiceInterface) {
    makeObservable(this)
  }

  @computed
  get hasCompanyPhones(): boolean {
    return !!this.company && this.company.phones.length > 0
  }

  @computed
  get companyPhones(): CompanyInfoByInn['phones'] {
    return this.company?.phones ?? []
  }

  @action.bound
  async fetchCompaniesByInn(inn: string): Promise<boolean> {
    if (!inn) {
      return false
    }

    this.isLoading = true

    try {
      const companies = await this.companyInfoService.getByInn(inn)
      this.companies = companies.filter(daDataCompanyFilter)
      this.openedModal = true
      return true
    } finally {
      this.isLoading = false
    }
  }

  @action.bound
  selectCompany(companyId: string): CompanyInfoByInn | null {
    const company = this.companies.find(c => c.id === companyId) ?? null
    this.company = company
    return company
  }

  @action.bound
  selectPhone(): void {
    // Сохраняем выбранный телефон (можно расширить при необходимости)
  }

  @action.bound
  showModal(): void {
    this.openedModal = true
  }

  @action.bound
  closeModal(): void {
    this.openedModal = false
  }

  @action.bound
  showPhoneModal(): void {
    this.openedPhoneModal = true
  }

  @action.bound
  closePhoneModal(): void {
    this.openedPhoneModal = false
  }

  @action.bound
  reset(): void {
    this.company = null
    this.companies = []
    this.isLoading = false
    this.openedModal = false
    this.openedPhoneModal = false
  }
}

@injectable()
export class RecipientFormStrategyFactory implements RecipientStrategyFactory {
  constructor(@inject(DI.CompanyInfoServiceInterface) private companyInfoService: CompanyInfoServiceInterface) {}

  create(type: 'person'): RecipientFormStrategyInterface
  create(type: 'company'): CompanyRecipientStrategyInterface
  create(type: 'person' | 'company'): RecipientFormStrategyInterface | CompanyRecipientStrategyInterface {
    if (type === 'company') {
      return new CompanyRecipientStrategy(this.companyInfoService)
    }
    return new PersonRecipientStrategy()
  }
}
